import type {
  GeneratedImageResult,
  ImageGenParams,
  ImageProvider,
  ImageProviderConfig,
} from "@roqeet/core";

export class ComfyImageProvider implements ImageProvider {
  readonly kind = "comfy";
  readonly supportsProgress = true;

  async generate(
    params: ImageGenParams,
    config: ImageProviderConfig,
    onProgress?: (pct: number, previewBlob?: Blob) => void,
  ): Promise<GeneratedImageResult[]> {
    const base = (config.endpoint ?? "http://127.0.0.1:8188").replace(/\/+$/, "");
    const extra = params.extra ?? {};
    const workflow = extra["workflow"] ?? extra["prompt"];
    if (!isRecord(workflow)) {
      throw new Error("ComfyUI requires a workflow JSON in params.extra.workflow.");
    }

    const prompt = applyWorkflowOverrides(workflow, params, extra);
    const clientId = String(extra["clientId"] ?? randomId());
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(config.extraHeaders ?? {}),
    };
    const queueBody = {
      prompt,
      client_id: clientId,
      ...(isRecord(extra["extraData"]) ? { extra_data: extra["extraData"] } : {}),
    };

    const init: RequestInit = { method: "POST", headers, body: JSON.stringify(queueBody) };
    if (params.signal) init.signal = params.signal;

    const queue = await fetch(`${base}/prompt`, init);
    if (!queue.ok) {
      const text = await queue.text();
      throw new Error(`ComfyUI image generation failed (HTTP ${queue.status}): ${text.slice(0, 400)}`);
    }
    const queued = (await queue.json()) as { prompt_id?: string };
    if (!queued.prompt_id) throw new Error("ComfyUI did not return a prompt_id.");

    onProgress?.(5);
    const timeoutMs = Number(extra["timeoutMs"] ?? 120_000);
    const pollMs = Number(extra["pollMs"] ?? 1_000);
    const started = Date.now();

    while (Date.now() - started < timeoutMs) {
      if (params.signal?.aborted) throw new Error("ComfyUI image generation aborted.");
      const historyRes = await fetch(`${base}/history/${encodeURIComponent(queued.prompt_id)}`, {
        headers: config.extraHeaders,
        ...(params.signal ? { signal: params.signal } : {}),
      });
      if (historyRes.ok) {
        const history = await historyRes.json();
        const images = extractImages(history, queued.prompt_id);
        if (images.length > 0) {
          const results: GeneratedImageResult[] = [];
          for (const image of images) {
            const url = new URL(`${base}/view`);
            url.searchParams.set("filename", image.filename);
            if (image.subfolder) url.searchParams.set("subfolder", image.subfolder);
            if (image.type) url.searchParams.set("type", image.type);
            const view = await fetch(url.toString(), {
              headers: config.extraHeaders,
              ...(params.signal ? { signal: params.signal } : {}),
            });
            if (!view.ok) continue;
            results.push({
              blob: await view.blob(),
              provenance: {
                provider: config.providerId ?? this.kind,
                model: config.defaultModel ?? "comfy-workflow",
                prompt: params.prompt,
                ...(params.negativePrompt ? { negativePrompt: params.negativePrompt } : {}),
                ...(params.seed != null ? { seed: params.seed } : {}),
                ...(params.width ? { width: params.width } : {}),
                ...(params.height ? { height: params.height } : {}),
                rawParams: { prompt_id: queued.prompt_id, workflow: prompt },
              },
            });
          }
          if (results.length > 0) {
            onProgress?.(100, results[0]?.blob);
            return results;
          }
        }
      }
      const pct = Math.min(95, 5 + Math.floor(((Date.now() - started) / timeoutMs) * 90));
      onProgress?.(pct);
      await sleep(pollMs, params.signal);
    }

    throw new Error(`ComfyUI image generation timed out after ${timeoutMs}ms.`);
  }
}

interface ComfyImageRef {
  filename: string;
  subfolder?: string;
  type?: string;
}

function applyWorkflowOverrides(
  workflow: Record<string, unknown>,
  params: ImageGenParams,
  extra: Record<string, unknown>,
): Record<string, unknown> {
  const next = cloneJson(workflow);
  setNodeInput(next, extra["promptNodeId"], "text", params.prompt);
  if (params.negativePrompt) setNodeInput(next, extra["negativePromptNodeId"], "text", params.negativePrompt);
  if (params.seed != null) setNodeInput(next, extra["seedNodeId"], "seed", params.seed);
  if (params.width) setNodeInput(next, extra["sizeNodeId"], "width", params.width);
  if (params.height) setNodeInput(next, extra["sizeNodeId"], "height", params.height);

  const overrides = extra["workflowInputOverrides"];
  if (isRecord(overrides)) {
    for (const [nodeId, inputs] of Object.entries(overrides)) {
      if (!isRecord(inputs)) continue;
      for (const [key, value] of Object.entries(inputs)) setNodeInput(next, nodeId, key, value);
    }
  }
  return next;
}

function setNodeInput(
  workflow: Record<string, unknown>,
  nodeId: unknown,
  inputName: string,
  value: unknown,
): void {
  if (!nodeId || value === undefined) return;
  const node = workflow[String(nodeId)];
  if (!isRecord(node)) return;
  if (!isRecord(node["inputs"])) node["inputs"] = {};
  (node["inputs"] as Record<string, unknown>)[inputName] = value;
}

function extractImages(history: unknown, promptId: string): ComfyImageRef[] {
  const root = isRecord(history) ? history : {};
  const entry = isRecord(root[promptId]) ? root[promptId] : root;
  const outputs = isRecord(entry["outputs"]) ? entry["outputs"] : {};
  const refs: ComfyImageRef[] = [];
  for (const output of Object.values(outputs)) {
    if (!isRecord(output) || !Array.isArray(output["images"])) continue;
    for (const image of output["images"]) {
      if (!isRecord(image) || typeof image["filename"] !== "string") continue;
      refs.push({
        filename: image["filename"],
        ...(typeof image["subfolder"] === "string" ? { subfolder: image["subfolder"] } : {}),
        ...(typeof image["type"] === "string" ? { type: image["type"] } : {}),
      });
    }
  }
  return refs;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function randomId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `roqeet-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("ComfyUI image generation aborted."));
      return;
    }
    const timer = globalThis.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        globalThis.clearTimeout(timer);
        reject(new Error("ComfyUI image generation aborted."));
      },
      { once: true },
    );
  });
}
